import { useState, useMemo, useEffect } from 'react'
import { ProductCard } from './components/ProductCard'
import { SearchFilters } from 'pages/Toolbox/StandardParts/components/dataExplorer/SearchFilters'
import {
  ContextSearchResult,
  ContextSearchResults,
} from 'store/types/searchResults'
import { componentPermissions } from 'config/permissions'
import { RoutePaths } from 'routes/paths'
import { componentNames, Roles } from 'config/roles'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { sagaGetSearchResultsAction } from 'saga/actions/assembler'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'pages/Toolbox/StandardParts/components/ui/pagination'
import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import { useNavigate } from 'react-router-dom'
import { userReducerState } from 'store/slices/user'

const ELECTRONICS_CONTEXT = '.electronics'

const ProductListPage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [classNameFilter, setClassNameFilter] = useState('all')
  const [groupFilter, setGroupFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(sagaGetSearchResultsAction(ELECTRONICS_CONTEXT))
  }, [dispatch])

  const searchResults: ContextSearchResults | undefined = useSelector(
    (state: RootState) => state.searchResults.searchResults?.data
  )

  const electronicsResults: ContextSearchResult[] = Array.isArray(searchResults)
    ? searchResults.filter((item) => item.contextName === ELECTRONICS_CONTEXT)
    : []

  const classNames = useMemo<string[]>(() => {
    return Array.from(
      new Set(
        electronicsResults
          .map((p: ContextSearchResult) => p.fullContext?.className)
          .filter((x): x is string => typeof x === 'string')
      )
    )
  }, [electronicsResults])

  const groups = useMemo<string[]>(() => {
    const productsToFilter =
      classNameFilter === 'all'
        ? electronicsResults
        : electronicsResults.filter(
            (p) => p.fullContext?.className === classNameFilter
          )

    const groupsArray = productsToFilter
      .map((p) => p.fullContext?.group)
      .filter((g): g is string => Boolean(g))

    return Array.from(new Set(groupsArray))
  }, [classNameFilter, electronicsResults])

  const filteredProducts = useMemo(() => {
    if (!electronicsResults.length) return []
    return electronicsResults.filter((product: ContextSearchResult) => {
      const matchesSearch =
        searchQuery === '' ||
        JSON.stringify(product.fullContext ?? {})
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesClass =
        classNameFilter === 'all' ||
        product.fullContext?.className === classNameFilter

      const matchesGroup =
        groupFilter === 'all' || product.fullContext?.group === groupFilter

      return matchesSearch && matchesClass && matchesGroup
    })
  }, [searchQuery, classNameFilter, groupFilter, electronicsResults])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, classNameFilter, groupFilter])

  useEffect(() => {
    if (groupFilter !== 'all' && !groups.includes(groupFilter)) {
      setGroupFilter('all')
    }
  }, [classNameFilter, groups, groupFilter])

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentProducts = filteredProducts.slice(startIndex, endIndex)

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const showEllipsis = totalPages > 7

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(totalPages)
    } else if (currentPage >= totalPages - 2) {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(totalPages)
    }
    return pages
  }

  const { roles } = useSelector(userReducerState)
  const navigate = useNavigate()
  useEffect(() => {
    const allowed =
      roles?.includes(Roles.ROLE_COMMERCIAL_DB_ELECTRONICS) ||
      roles?.includes(Roles.ROLE_COMMERCIAL_API_ELECTRONICS) ||
      roles?.includes(Roles.ROLE_USER_ADMIN)
    if (!allowed) {
      navigate(RoutePaths.billing)
    }
  }, [roles, navigate])

  return (
    <ContentBodyWrapper title={'Electronics Explorer'}>
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8">
          <p className="text-lg text-muted-foreground">
            Browse electronic components with calculated assembly models
          </p>
        </header>

        <div className="mb-8">
          <SearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            classNameFilter={classNameFilter}
            onClassNameChange={setClassNameFilter}
            groupFilter={groupFilter}
            onGroupChange={setGroupFilter}
            classNames={classNames}
            groups={groups}
          />
        </div>

        <div className="mb-4 text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)}{' '}
          of {filteredProducts.length} products
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {currentProducts.map((product: ContextSearchResult) => (
            <ProductCard key={product.sid} product={product} />
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground">
              No products found matching your filters.
            </p>
          </div>
        )}

        {filteredProducts.length > 0 && totalPages > 1 && (
          <div className="mt-8">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className={
                      currentPage === 1
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>

                {getPageNumbers().map((page, index) => (
                  <PaginationItem key={index}>
                    {page === 'ellipsis' ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        onClick={() => setCurrentPage(page as number)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    className={
                      currentPage === totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </ContentBodyWrapper>
  )
}

const electronicsV2ListPage = {
  routeProps: {
    path: RoutePaths.toolboxElectronicsV2,
    title: 'Electronics Explorer',
    element: <ProductListPage />,
    roles: componentPermissions[componentNames.pageElectronicsV2],
    requiresAuthentication: true,
  },
  name: 'Electronics V2',
}

export default electronicsV2ListPage
